import { Box, Button, Container, Grid, MenuItem, Paper, Select, Typography } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import React, { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { numberFormatter } from '../../../utils/formatters'
import { getRebatePercentage } from '../../../utils/rebatePercentage'
import TyreView from '../tyre/TyreView'
import { tyreLocationsObject } from '../../../utils/tyreLocations'
import { useAuth } from '../../../hooks/context/UserAuthContext'
import { useUpdateWarrantyClaimStatus } from '../../../hooks/api/warranty-claims'
import Status from '../../ui/Status'

const WarrantyClaimView = ({ data, refetch, warrantyRegistration = data.warrantyRegistration }) => {
  const { user } = useAuth()
  const { mutateAsync: updateStatus } = useUpdateWarrantyClaimStatus()
  const [status, setStatus] = useState(data.status)

  const handleUpdateStatus = async (event) => {
    try {
      const newStatus = event.target.value
      await updateStatus({ claimId: data._id, status: newStatus })
      setStatus(newStatus)
      refetch?.()
    } catch (err) {
      console.error(err)
    }
  }
  return (
    <Paper variant='outlined' sx={{ p: 1, my: 2 }}>
      <Box display='flex'>
        <Status status={status} />
        <Box flexGrow={1} />
        {user?.isAdmin && user?.isHTAStaff && status === 'Processing' && (
          <Box display='flex' alignItems='center'>
            <Typography component='span' variant='body1' sx={{ mr: 1 }}>
              Update status:{' '}
            </Typography>
            <Select onChange={handleUpdateStatus} value={status} variant='standard'>
              <MenuItem value='Processing'>Processing</MenuItem>
              <MenuItem value='Approved'>Approved</MenuItem>
              <MenuItem value='Rejected'>Rejected</MenuItem>
            </Select>
          </Box>
        )}
      </Box>
      <Grid container rowGap={1}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant='subtitle1'>Claim ID</Typography>
          <Typography variant='body2' color='textSecondary'>
            {data._id}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant='subtitle1'>Warranty Claim Date</Typography>
          <Typography variant='body2' color='textSecondary'>
            {format(parseISO(data.createdAt), 'dd/MM/yyyy')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant='subtitle1'>Claimant Email Address</Typography>
          <Typography variant='body2' color='textSecondary'>
            {data.claimant?.email}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant='subtitle1'>Claim Tyre Axle Locations</Typography>
          <Typography variant='body2' color='textSecondary'>
            {data.claimLocations
              .sort()
              .map((loc) => tyreLocationsObject[loc])
              ?.join(', ')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant='subtitle1'>Odometer Reading</Typography>
          <Typography variant='body2' color='textSecondary'>
            {numberFormatter.format(data.mileage)} km
          </Typography>
        </Grid>
        {!isNaN(warrantyRegistration.mileageOnPurchase) && (
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='subtitle1'>Total Mileage</Typography>
            <Typography variant='body2' color='textSecondary'>
              {numberFormatter.format(data.mileage - warrantyRegistration.mileageOnPurchase)} km
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <Typography variant='subtitle1'>Rebate</Typography>
          <Typography variant='body2' color='textSecondary'>
            {data.rebatePercentage ||
              getRebatePercentage({
                dealershipBrand: warrantyRegistration.customer?.dealershipBrand,
                dateOfPurchase: parseISO(warrantyRegistration.dateOfPurchase),
                createdAt: parseISO(data.createdAt),
                mileage: data.mileage,
                mileageOnPurchase: warrantyRegistration.mileageOnPurchase,
              })}
          </Typography>
        </Grid>
        {data.photoOdometer && (
          <Grid item xs={12} md={4} sx={{ p: 1 }}>
            <Button
              fullWidth
              variant='outlined'
              component='a'
              href={data.photoOdometer.filePath}
              target='_blank'
              rel='noopener noreferrer'
              startIcon={<FileDownloadIcon />}
            >
              Odometer Photo
            </Button>
          </Grid>
        )}
        {data.photoLicensePlate && (
          <Grid item xs={12} md={4} sx={{ p: 1 }}>
            <Button
              fullWidth
              variant='outlined'
              component='a'
              href={data.photoLicensePlate.filePath}
              target='_blank'
              rel='noopener noreferrer'
              startIcon={<FileDownloadIcon />}
            >
              License Plate Photo
            </Button>
          </Grid>
        )}
        {data.docPurchaseOrder && (
          <Grid item xs={12} md={4} sx={{ p: 1 }}>
            <Button
              fullWidth
              variant='outlined'
              component='a'
              href={data.docPurchaseOrder.filePath}
              target='_blank'
              rel='noopener noreferrer'
              startIcon={<FileDownloadIcon />}
            >
              Purchase Order Document
            </Button>
          </Grid>
        )}
      </Grid>
      {warrantyRegistration.tyres && (
        <Box>
          <Typography component='span' variant='h6'>
            Claimed tyres
          </Typography>
          <Grid container>
            {data.claimLocations.sort().map((loc) => (
              <Grid key={loc} item xs={12} sm={6} md={3}>
                <Typography variant='subtitle1'>{tyreLocationsObject[loc]}</Typography>
                {data.photoTyreSidewall?.[loc] && (
                  <Box p={1}>
                    <Button
                      fullWidth
                      variant='outlined'
                      component='a'
                      href={data.photoTyreSidewall[loc].filePath}
                      target='_blank'
                      rel='noopener noreferrer'
                      startIcon={<FileDownloadIcon />}
                    >
                      Tyre Sidewall Photo
                    </Button>
                  </Box>
                )}
                {data.photoTyreLocation?.[loc] && (
                  <Box p={1}>
                    <Button
                      fullWidth
                      variant='outlined'
                      component='a'
                      href={data.photoTyreLocation[loc].filePath}
                      target='_blank'
                      rel='noopener noreferrer'
                      startIcon={<FileDownloadIcon />}
                    >
                      Tyre Location Photo
                    </Button>
                  </Box>
                )}
                <TyreView
                  data={warrantyRegistration.tyres?.[loc]}
                  sx={{ m: 1 }}
                  variant='outlined'
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Paper>
  )
}

export default WarrantyClaimView
